export const SELECTION_TYPE = {
  NONE: "none",
  SINGLE: "single",
  MULTIPLE: "multiple",
};

/**
 * Canonical wizard step configuration.
 *
 * Step identity is a stable string id (not a numeric position) so the order,
 * names, and presence of steps can be configured per `template.id` without the
 * numeric-scheme collisions the wizard used to have. Navigation is derived
 * from the ordered, template-resolved list: Back/Continue simply move to the
 * previous/next enabled step.
 *
 * Image variations steps are the one exception to "one step id per step": a
 * template's `imageVariations` may carry an optional `stage` (defaulting to
 * `"default"`), and every stage gets its own consecutive step — `images` for
 * the default stage, `images-<stage>` for the rest. See `resolveSteps`. They
 * are told apart by their `type` (`STEP_TYPE.IMAGES`), never by their id.
 *
 * This module is pure data + helpers (no React) so it can be imported by both
 * the `useVideoWizard` hook and the view layer without cycles.
 */

export const WIZARD_STEP = {
  SELECT_TEMPLATE: "select-template",
  ACCOUNT_DETAILS: "account-details",
  CUSTOMER_DETAILS: "customer-details",
  CREATE_VIDEO: "create-video",
  CAMPAIGN: "campaign",
  STORY_FOUNDATION: "story-foundation",
  CAMPAIGN_VISUALS: "campaign-visuals",
  KEY_VISUALS: "key-visuals",
  IMAGES_AND_TEXT: "images-and-text",
  SCENES: "scenes",
  VOICEOVER: "voiceover",
  BRANDING: "branding",
  AUDIO: "audio",
  IMAGES: "images",
  IMAGES_PLANNING: "images-planning",
  BANNERS: "banners",
  GENERATE_VIDEO: "generate-video",
  FIGMA_HANDOFF: "figma-handoff",
};

/**
 * A step may declare a `type` to opt into the behaviour of a step family
 * regardless of its id, so a flow can hold several steps of the same kind.
 *
 * - `campaign-visuals`: renders the step's `campaignStructure` with its linked
 *   image variations, which the step generates. The `campaign-visuals` step
 *   is one implicitly. Its fine-tuning prompt (`showPromptFooter: false` hides
 *   it) suggests the campaign's `promptHintsKey` hints (`promptHints` by
 *   default), and an edit sent from it targets the story section named by
 *   `editScope` (the prompt's `[Edit-scope]`; the whole story when unset). The
 *   images of the step whose source the edit changed are then regenerated.
 * - `images`: renders and generates the template's image variations of the
 *   step's `stages` (every stage when it declares none). The `images` step is
 *   one implicitly; the stage steps `resolveSteps` appends are typed as such.
 *
 * The `branding` step may declare a `campaignStructure` too: its variations are
 * shown below the branding assets, but a variation that was never generated
 * waits for the user to generate it (see `isBrandingVariationsStep`).
 */
export const STEP_TYPE = {
  CAMPAIGN_VISUALS: "campaign-visuals",
  IMAGES: "images",
};

// The type a step has implicitly, by its id, when it declares none.
const IMPLICIT_STEP_TYPES = {
  [WIZARD_STEP.CAMPAIGN_VISUALS]: STEP_TYPE.CAMPAIGN_VISUALS,
  [WIZARD_STEP.IMAGES]: STEP_TYPE.IMAGES,
};

// Step helpers take a configured step entry; a bare id stands for a step that
// declares nothing but its id.
const toStepEntry = (step) =>
  typeof step === "string" ? { id: step } : (step ?? undefined);

/** The `type` of a configured step, falling back to the one its id implies. */
export const getStepType = (step) => {
  const entry = toStepEntry(step);
  return entry?.type ?? IMPLICIT_STEP_TYPES[entry?.id];
};

/** Whether a configured step is a campaign visuals step. */
export const isCampaignVisualsStep = (step) =>
  getStepType(step) === STEP_TYPE.CAMPAIGN_VISUALS;

/** Whether a configured step renders image variations (any stage). */
export const isImagesStep = (step) => getStepType(step) === STEP_TYPE.IMAGES;

/**
 * The id of the step family a configured step belongs to: every images step
 * shares the `images` identity for ordering, step→component and default action
 * lookups; any other step is its own family.
 */
export const getStepFamilyId = (step) =>
  isImagesStep(step) ? WIZARD_STEP.IMAGES : toStepEntry(step)?.id;

/**
 * Whether a configured step is a branding step whose `campaignStructure` shows
 * image variations. Unlike a campaign visuals step, it does not generate a
 * variation that was never generated until the user asks for it.
 */
export const isBrandingVariationsStep = (step) =>
  step?.id === WIZARD_STEP.BRANDING &&
  getStructureVariationKeys(step?.campaignStructure).length > 0;

/**
 * Master ordering across every possible step (the union of all flows). Used
 * only for ordinal "is this step at/after X" comparisons — NOT for a specific
 * template's flow (use `resolveSteps` for that).
 */
export const STEP_ORDER = [
  WIZARD_STEP.SELECT_TEMPLATE,
  WIZARD_STEP.ACCOUNT_DETAILS,
  WIZARD_STEP.CUSTOMER_DETAILS,
  WIZARD_STEP.CREATE_VIDEO,
  WIZARD_STEP.CAMPAIGN,
  WIZARD_STEP.STORY_FOUNDATION,
  WIZARD_STEP.CAMPAIGN_VISUALS,
  WIZARD_STEP.KEY_VISUALS,
  WIZARD_STEP.IMAGES_AND_TEXT,
  WIZARD_STEP.SCENES,
  WIZARD_STEP.VOICEOVER,
  WIZARD_STEP.BRANDING,
  WIZARD_STEP.AUDIO,
  WIZARD_STEP.IMAGES,
  WIZARD_STEP.BANNERS,
  WIZARD_STEP.GENERATE_VIDEO,
  WIZARD_STEP.FIGMA_HANDOFF,
];

/** A grid of campaign fields (the default) or of bare image variations. */
export const GRID_TYPE = {
  FIELDS: "fields",
  VARIATIONS: "variations",
};

/**
 * Whether a `variations` grid cell is a text cell — a campaign field (its
 * `value` a campaign key, or a list of them) rather than an image.
 */
export const isTextCell = (cell) =>
  Array.isArray(cell?.value)
    ? cell.value.length > 0
    : typeof cell?.value === "string" && !!cell.value;

const LINK_TYPE = {
  LINK: "link",
  BUTTON: "button",
  EMBED: "embed",
};

const EMBED_ASPECT_RATIO = "4 / 3";

// What an embedded page may do. Scripts together with the same origin would
// let a same-origin page lift its own sandbox, so that one is left out then.
const EMBED_SANDBOX = [
  "allow-scripts",
  "allow-forms",
  "allow-popups",
  "allow-popups-to-escape-sandbox",
];

// Absolute http(s) and mailto URLs, and relative ones — nothing that runs code.
const SAFE_LINK = /^(https?:|mailto:|\/|\.{1,2}\/|#|\?)/i;
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

// Links open in a named window, so a click navigates the tab an earlier one
// opened instead of opening another. The browser only finds that tab when the
// link is not `noopener`/`noreferrer`.
const LINK_WINDOW_NAME = "bestory-links";

/** Whether a URL is on the origin of the app (relative ones always are). */
const isSameOrigin = (href) => {
  try {
    return (
      typeof window === "undefined" ||
      new URL(href, window.location.href).origin === window.location.origin
    );
  } catch {
    return true;
  }
};

/**
 * The window a link opens in: `linkTarget` when set — `"_blank"` always opens
 * a new tab, `"_self"` stays put, anything else names the window — else the
 * one shared by all links, or by those of the same `linkGroup`.
 */
const getLinkTarget = (cell) => {
  if (typeof cell.linkTarget === "string" && cell.linkTarget) {
    return cell.linkTarget;
  }

  const group =
    typeof cell.linkGroup === "string"
      ? cell.linkGroup
          .toLowerCase()
          .replace(/[^a-z0-9_-]+/g, "-")
          .replace(/^-+|-+$/g, "")
      : "";

  return group ? `${LINK_WINDOW_NAME}-${group}` : LINK_WINDOW_NAME;
};

/**
 * The link a grid cell shows, from its `link` (the href), `linkLabel` (the
 * text, the URL by default), `linkType` (`"link"` by default, `"button"`, or
 * `"embed"` for an iframe), `linkTarget` and `linkGroup` (see
 * `getLinkTarget`). Only a link opening in `"_blank"` is `noopener
 * noreferrer`. `null` when the cell has no link, or one that is not an
 * http(s)/mailto/relative URL.
 *
 * An embed also has an `aspectRatio` (`linkAspectRatio`, 4 / 3 by default) and
 * a `sandbox` (`linkSandbox`, `""` for the strictest) — and, being a frame, no
 * `mailto:`: such a link is shown as a plain one.
 */
export const getCellLink = (cell) => {
  const href = typeof cell?.link === "string" ? cell.link.trim() : "";

  if (!href || !(SAFE_LINK.test(href) || !HAS_SCHEME.test(href))) {
    return null;
  }

  const target = getLinkTarget(cell);

  const isEmbed = cell.linkType === LINK_TYPE.EMBED && !/^mailto:/i.test(href);

  const type = isEmbed
    ? LINK_TYPE.EMBED
    : cell.linkType === LINK_TYPE.BUTTON
      ? LINK_TYPE.BUTTON
      : LINK_TYPE.LINK;

  return {
    href,
    label:
      typeof cell.linkLabel === "string" && cell.linkLabel
        ? cell.linkLabel
        : href,
    type,
    target,
    rel: target === "_blank" ? "noopener noreferrer" : undefined,
    ...(isEmbed
      ? {
          aspectRatio:
            typeof cell.linkAspectRatio === "string" && cell.linkAspectRatio
              ? cell.linkAspectRatio
              : EMBED_ASPECT_RATIO,
          sandbox:
            typeof cell.linkSandbox === "string"
              ? cell.linkSandbox
              : (isSameOrigin(href)
                  ? EMBED_SANDBOX
                  : ["allow-same-origin", ...EMBED_SANDBOX]
                ).join(" "),
        }
      : {}),
  };
};

/**
 * Whether dropping an image onto the variation a cell shows is disabled: its
 * own `disableDrop` wins over its grid's.
 */
export const isCellDropDisabled = (cell, grid) =>
  [cell?.disableDrop, grid?.disableDrop].find(
    (value) => typeof value === "boolean",
  ) ?? false;

/**
 * The grids of a `campaignStructure` group, as `{ type, title, description,
 * rows }` where each row is a list of cells laid out as equal-width columns.
 *
 * A group either defines `grid` — a list of `{ type, title, description,
 * rows }` — or the original flat `fields`, which is a single untitled grid
 * with one field per row. `grid` takes precedence over `fields`.
 *
 * The cells of a `fields` grid are campaign fields (`{ label, value,
 * variationKey?, assetName?, ... }`); those of a `variations` grid are
 * `{ variationKey?, referenceKey?, assetName?, label?, description? }`, an
 * image variation, reference image and/or template asset shown without any
 * campaign text, where `label` and `description` override the variation's (or
 * asset's) own caption — `null` hiding it. Fields may carry a `referenceKey`
 * too — a single asset name or a list of them, as in
 * `template.imageVariations`; their `label` is the text's, so what they link to
 * keeps its own caption.
 *
 * `assetName` names an image or color asset of `template.data.assets`, which
 * the user fills (uploads, picks) right there, as in `TemplateAssets`.
 * `link` shows a link — `linkLabel` its text, `linkType` `"link"`, `"button"`
 * or `"embed"` (an iframe) — after the cell's other items, under the same
 * `label` and `description`. Links share one tab by default, or one per `linkGroup`;
 * `linkTarget` overrides where one opens (see `getCellLink`).
 *
 * A field or cell showing an image variation may name the campaign keys its
 * image is generated from with `campaignKey` (a key or a list of them),
 * extending the template variation's own `campaignKey` — and, for a field, its
 * `value`. When one of them changes, the image is flagged as outdated (see
 * `variationDependencies`).
 *
 * A `variations` grid cell may instead be a text cell, `{ label, value, size?,
 * disabled? }` (see `isTextCell`): a campaign field shown on its own, boxed
 * like a `fields` grid. It shows no image — any image keys it carries are
 * ignored.
 *
 * A `null` cell of a `grid` row is kept, as an empty column placeholder.
 *
 * Any cell may span several columns with `colSpan` and several rows with
 * `rowSpan` (both 1 by default). A `fields` row has as many columns as its
 * cells' spans add up to (at most 5); a `variations` row always has 5. The
 * rows a cell spans down count its columns as taken, laying their own cells
 * out around it. See `layoutGridRows`.
 *
 * A `fields` grid whose fields link images shows them beside its fields, on
 * the 5 columns of a `variations` grid: `linkedColSpan` of them for the images
 * (1-4, 2 by default), the rest for the fields — whose rows are then laid out
 * on just those columns, the last field of a line filling what is left of it.
 *
 * An image variation's card accepts a dropped image to replace its own, unless
 * `disableDrop` is set on its cell or, for all of the cells without one, on
 * the grid (see `isCellDropDisabled`) — the idle card then offers an upload.
 *
 * A `grid` may list `previewLinks` — `{ label, href? }` — shown beside its
 * title (see `getPreviewLinks`).
 */
export const getGroupGrids = (group) => {
  if (group?.grid) {
    return (Array.isArray(group.grid) ? group.grid : [group.grid])
      .filter(Boolean)
      .map(
        ({
          type = GRID_TYPE.FIELDS,
          title,
          description,
          linkedColSpan,
          disableDrop,
          previewLinks,
          rows,
        }) => ({
          type,
          title,
          description,
          linkedColSpan,
          disableDrop,
          previewLinks,
          rows: (rows ?? [])
            .map((row) =>
              (Array.isArray(row) ? row : [row]).map((cell) => cell ?? null),
            )
            .filter((row) => row.length > 0),
        }),
      );
  }

  return [
    {
      type: GRID_TYPE.FIELDS,
      rows: (group?.fields ?? []).filter(Boolean).map((field) => [field]),
    },
  ];
};

/**
 * The URL a preview link points to, from the template's general `previewUrl`
 * and the link's `href`:
 * - no `href`: the `previewUrl`;
 * - an `href` of just a hash and/or search (`"#slide-2"`, `"?lang=de"`,
 *   `"?lang=de#slide-2"`): the `previewUrl` with its hash replaced and the
 *   search params added or replaced — each only when the `href` has it;
 * - any other `href` (a full URL with a domain, or a path): used as it is.
 * `null` for a URL that is not http(s) or relative, or when there is no
 * `previewUrl` to base the link on.
 */
const resolvePreviewHref = (href, previewUrl) => {
  const value = typeof href === "string" ? href.trim() : "";
  const base = typeof previewUrl === "string" ? previewUrl.trim() : "";

  if (value && !/^[#?]/.test(value)) {
    return /^https?:/i.test(value) ||
      (!HAS_SCHEME.test(value) && SAFE_LINK.test(value))
      ? value
      : null;
  }

  if (!base || !/^https?:/i.test(base)) {
    return null;
  }

  if (!value) {
    return base;
  }

  try {
    const url = new URL(base);
    const hashIdx = value.indexOf("#");
    const search = hashIdx === -1 ? value : value.slice(0, hashIdx);

    if (search.startsWith("?")) {
      const seen = new Set();

      // `set` replaces a param in place, keeping the order of the rest.
      for (const [key, val] of new URLSearchParams(search)) {
        if (seen.has(key)) {
          url.searchParams.append(key, val);
        } else {
          seen.add(key);
          url.searchParams.set(key, val);
        }
      }
    }

    if (hashIdx !== -1) {
      url.hash = value.slice(hashIdx);
    }

    return url.toString();
  } catch {
    return null;
  }
};

/**
 * The preview links of a grid, as `{ label, href, target, rel }`, from its
 * `previewLinks` (`{ label, href?, linkTarget?, linkGroup? }`) and the
 * template's general `previewUrl` (see `resolvePreviewHref` for what `href` may
 * be). A link without a `label` shows its URL; one whose URL cannot be
 * resolved is left out. Like the links of a grid's cells, they open in the one
 * named window the others share, or in their `linkGroup`'s or `linkTarget`
 * (see `getLinkTarget`).
 */
export const getPreviewLinks = (previewLinks, previewUrl) =>
  (Array.isArray(previewLinks) ? previewLinks : [previewLinks])
    .filter((link) => link && typeof link === "object")
    .map((link) => {
      const href = resolvePreviewHref(link.href, previewUrl);

      if (!href) {
        return null;
      }

      const target = getLinkTarget(link);

      return {
        href,
        label: typeof link.label === "string" && link.label ? link.label : href,
        target,
        rel: target === "_blank" ? "noopener noreferrer" : undefined,
      };
    })
    .filter(Boolean);

/**
 * The cells of a grid's rows that may show an image — every cell but the empty
 * ones and, in a `variations` grid, its text cells.
 */
const getImageCells = (type, rows) =>
  rows
    .flat()
    .filter(Boolean)
    .filter((cell) => type !== GRID_TYPE.VARIATIONS || !isTextCell(cell));

/**
 * The asset keys a `campaignStructure` shows as references (`referenceKey` of
 * a field or a variations grid cell), in order of appearance. They are only
 * displayed — unlike variations, the step does not generate them.
 */
export const getStructureReferenceKeys = (campaignStructure) => {
  const keys = new Set();

  for (const group of campaignStructure ?? []) {
    for (const { type, rows } of getGroupGrids(group)) {
      for (const cell of getImageCells(type, rows)) {
        getVariationReferenceKeys(cell).forEach((key) => keys.add(key));
      }
    }
  }

  return Array.from(keys);
};

/**
 * The keys of every image variation a `campaignStructure` shows — linked to a
 * field or listed in a `variations` grid — in order of appearance.
 */
export const getStructureVariationKeys = (campaignStructure) => {
  const keys = new Set();

  for (const group of campaignStructure ?? []) {
    for (const { type, rows } of getGroupGrids(group)) {
      for (const cell of getImageCells(type, rows)) {
        for (const key of ["variationKey", "referenceKey"]) {
          if (cell[key]) {
            keys.add(cell[key]);
          }
        }
      }
    }
  }

  return Array.from(keys);
};

/**
 * Whether an image variation a `campaignStructure` shows is still missing its
 * image. Only the given `variations` count — the ones the template actually
 * defines and enables for the campaign (`imageVariations.variations`).
 */
export const hasPendingStructureVariations = (
  campaignStructure,
  variations,
  assets,
) => {
  const linkedKeys = getStructureVariationKeys(campaignStructure);

  return (variations ?? [])
    .filter((variation) => linkedKeys.includes(variation.key))
    .some(
      (variation) => !assets?.some((asset) => asset.name === variation.key),
    );
};

/**
 * How many image variations a `campaignStructure` shows already have their
 * image (generated or uploaded), out of how many it shows — the ones the
 * template defines and enables for the campaign (`variations`, as for
 * `hasPendingStructureVariations`). A variation that is also a template asset
 * (`templateAssetNames`) is not counted, as the assets are counted already.
 */
export const getStructureVariationProgress = (
  campaignStructure,
  variations,
  assets,
  templateAssetNames = [],
) => {
  const linkedKeys = getStructureVariationKeys(campaignStructure);

  const shown = (variations ?? []).filter(
    (variation) =>
      linkedKeys.includes(variation.key) &&
      !templateAssetNames.includes(variation.key),
  );

  return {
    total: shown.length,
    selected: shown.filter((variation) =>
      assets?.some((asset) => asset.name === variation.key),
    ).length,
  };
};

/** The template asset names a `campaignStructure` shows, in order. */
export const getStructureAssetNames = (campaignStructure) => {
  const names = new Set();

  for (const group of campaignStructure ?? []) {
    for (const { type, rows } of getGroupGrids(group)) {
      for (const cell of getImageCells(type, rows)) {
        if (cell.assetName) {
          names.add(cell.assetName);
        }
      }
    }
  }

  return Array.from(names);
};

/**
 * Whether a required (not `isOptional`) image or color template asset a
 * `campaignStructure` shows is still missing from the video — the check
 * `areAllVisualsSelected` makes for every template asset.
 */
export const hasPendingStructureAssets = (
  campaignStructure,
  templateAssets,
  videoAssets,
) => {
  const names = getStructureAssetNames(campaignStructure);

  return (templateAssets ?? [])
    .filter(
      (asset) =>
        names.includes(asset.name) &&
        (asset.type === "image" || asset.type === "color") &&
        !asset.isOptional,
    )
    .some(
      (asset) =>
        !videoAssets?.some((a) => a.name === asset.name && !a.isInvalid),
    );
};

/**
 * Image variations are grouped into "stages" so the single images step can be
 * split into several consecutive steps. A variation without an explicit
 * `stage` belongs to the default stage — i.e. the first images step, which
 * keeps the original `WIZARD_STEP.IMAGES` id (and therefore its URL).
 */
export const DEFAULT_IMAGE_STAGE = "default";

/** The stage a template image variation belongs to. */
export const getVariationStage = (variation) =>
  variation?.stage ?? DEFAULT_IMAGE_STAGE;

/**
 * An image variation may reference more than one source image. Only the
 * Google models (`nano-banana*`, routed through the Firefly 3p endpoint)
 * accept multiple references, and they accept at most this many.
 */
export const MAX_IMAGE_REFERENCES = 6;

/**
 * A variation's reference image keys, normalized to an ordered, de-duplicated
 * array. `referenceKey` is either a single key (the original shape) or an
 * array of keys; anything beyond `MAX_IMAGE_REFERENCES` is dropped.
 *
 * The first key is the *primary* reference: the one whose seed, file and
 * labeling lead, and the one single-reference models fall back to.
 */
export const getVariationReferenceKeys = (variation) => {
  const raw = variation?.referenceKey;
  const keys = Array.from(
    new Set(
      (Array.isArray(raw) ? raw : [raw]).filter(
        (key) => typeof key === "string" && !!key,
      ),
    ),
  );

  if (keys.length > MAX_IMAGE_REFERENCES) {
    console.warn(
      `Image variation "${variation?.key}" has ${keys.length} reference images, only the first ${MAX_IMAGE_REFERENCES} are used.`,
    );
    return keys.slice(0, MAX_IMAGE_REFERENCES);
  }

  return keys;
};

/** The primary reference image key — the first one a variation references. */
export const getPrimaryReferenceKey = (variation) =>
  getVariationReferenceKeys(variation)[0] ?? null;

/**
 * Image variations are grouped by their whole `referenceKey`, so variations
 * built from the same set of reference images — in the same order — share a
 * group, and `["IMG1", "IMG2"]` is a group of its own rather than part of the
 * `"IMG1"` one.
 */
export const getReferenceGroupId = (referenceKeys) =>
  JSON.stringify(referenceKeys ?? []);

/** The id of the reference image group a variation belongs to. */
export const getVariationReferenceGroupId = (variation) =>
  getReferenceGroupId(getVariationReferenceKeys(variation));

/**
 * The distinct stages used by a template's image variations, in the order they
 * first appear — except the default stage, which always comes first when used.
 */
export const getImageStages = (templateData) => {
  const stages = [];

  for (const variation of templateData?.imageVariations ?? []) {
    const stage = getVariationStage(variation);
    if (!stages.includes(stage)) {
      stages.push(stage);
    }
  }

  return stages.includes(DEFAULT_IMAGE_STAGE)
    ? [
        DEFAULT_IMAGE_STAGE,
        ...stages.filter((stage) => stage !== DEFAULT_IMAGE_STAGE),
      ]
    : stages;
};

/**
 * Keep only the variations belonging to `stages`. A nullish `stages` means "no
 * stage filtering" so callers that aren't stage-aware keep seeing everything.
 */
export const filterVariationsByStages = (variations, stages) => {
  const list = variations ?? [];

  if (!stages) {
    return list;
  }

  const allowed = new Set(stages);
  return list.filter((variation) => allowed.has(getVariationStage(variation)));
};

/**
 * Whether a variation should be included for the current campaign. A variation
 * may carry a `condition` — an expression string eval'd against
 * `selectedCampaign` (same convention as an asset's `context`/use-case
 * conditions) — so a variation can be generated and shown only when a campaign
 * flag is set. No `condition` means always included.
 *
 * The parameter is named `selectedCampaign` on purpose: the `eval` resolves it
 * from this scope, so a condition reads e.g.
 * `selectedCampaign?.['Include-3D-Digital-Twin'] === true`.
 */
export const isVariationEnabled = (variation, selectedCampaign = {}) => {
  const condition = variation?.condition;

  if (!condition) {
    return true;
  }

  try {
    return !!new Function("selectedCampaign", `return (${condition});`)(
      selectedCampaign,
    );
  } catch (error) {
    console.warn("Failed to evaluate variation condition:", variation, error);
    return false;
  }
};

/**
 * The variations of `stages` that also pass their campaign `condition` — the
 * set that should actually render and generate. Combines the stage filter with
 * `isVariationEnabled` so both call sites stay in sync.
 */
export const filterActiveVariations = (variations, stages, selectedCampaign) =>
  filterVariationsByStages(variations, stages).filter((variation) =>
    isVariationEnabled(variation, selectedCampaign),
  );

/** The stages an image variation goes through while it is being produced. */
export const VARIATION_PROGRESS_STAGE = {
  // A reference image being described (reference images only).
  LABELING: "labeling",
  // Waiting for a prompt, a reference, or a free slot to start generating.
  WAITING: "waiting",
  GENERATING: "generating",
  SAVING: "saving",
};

/** The wizard step id that renders a given image variations stage. */
export const makeImagesStepId = (stage) =>
  !stage || stage === DEFAULT_IMAGE_STAGE
    ? WIZARD_STEP.IMAGES
    : `${WIZARD_STEP.IMAGES}-${stage}`;

/** Stepper label for a stage that has no explicitly configured step. */
export const getStageStepName = (stage) => {
  const label = String(stage ?? "")
    .replace(/[-_]+/g, " ")
    .trim();

  return label && stage !== DEFAULT_IMAGE_STAGE
    ? `${label.charAt(0).toUpperCase()}${label.slice(1)} variations`
    : "Images variations";
};

// A null/unknown step sorts before every real step (index -1), matching the
// old numeric behaviour where `currentStep` defaulted to ~0. Pass the step
// entry for a step outside `STEP_ORDER` (e.g. an images stage step) — its
// family decides where it sorts.
export const stepIndex = (step) => STEP_ORDER.indexOf(getStepFamilyId(step));
export const isStepAtOrAfter = (a, b) => stepIndex(a) >= stepIndex(b);
export const isStepAfter = (a, b) => stepIndex(a) > stepIndex(b);
export const isStepAtOrBefore = (a, b) => stepIndex(a) <= stepIndex(b);
export const isStepBefore = (a, b) => stepIndex(a) < stepIndex(b);

// Template predicates that gate conditional steps.
export const hasAudioStep = (templateData) =>
  !!templateData?.assets?.some((asset) => asset.type === "audio");

export const hasImageVariationsStep = (templateData) =>
  (templateData?.imageVariations?.length ?? 0) > 0;

/**
 * `enabled` predicate factory for an images step restricted to `stages`: the
 * step only applies when the template actually defines variations in them.
 */
export const hasImageVariationsStage = (stages) => (templateData) =>
  filterVariationsByStages(templateData?.imageVariations, stages).length > 0;

export const hasBannersStep = (templateData) =>
  !!templateData?.assetsTemplateId;

/**
 * Default flow, modelled on the "ucx" template:
 * customer → campaign focus → (voiceover) → brands & visuals → (images) →
 * (banners) → video.
 */
const DEFAULT_STEPS = [
  { id: WIZARD_STEP.SELECT_TEMPLATE, name: "Template selection" },
  { id: WIZARD_STEP.ACCOUNT_DETAILS, name: "Account details" },
  { id: WIZARD_STEP.CUSTOMER_DETAILS, name: "Customer details" },
  { id: WIZARD_STEP.CREATE_VIDEO, name: "Creating project", viewless: true },
  {
    id: WIZARD_STEP.CAMPAIGN,
    name: "Campaign focus",
    preview: "campaign",
    contentLabel: "campaign",
    canEditCampaign: true,
  },
  { id: WIZARD_STEP.VOICEOVER, name: "Voiceover", enabled: hasAudioStep },
  { id: WIZARD_STEP.BRANDING, name: "Brands & visuals" },
  // The "planning" stage renders first as its own step, so a template can
  // generate its key visuals / reference anchors (which the default-stage
  // variations reference) before the main fan-out. Opt in purely via data by
  // tagging variations with `stage: "planning"`; templates without any such
  // variation skip this step entirely.
  {
    id: WIZARD_STEP.IMAGES_PLANNING,
    type: STEP_TYPE.IMAGES,
    name: "Key visuals",
    stages: ["planning"],
    enabled: hasImageVariationsStage(["planning"]),
  },
  // Renders the default image stage. Stages beyond these two are appended
  // automatically by `resolveSteps`; add an explicit entry here (e.g.
  // `{ id: makeImagesStepId("video"), type: STEP_TYPE.IMAGES,
  //    name: "Video variations", stages: ["video"],
  //    enabled: hasImageVariationsStage(["video"]) }`)
  // to control a stage's name or position.
  {
    id: WIZARD_STEP.IMAGES,
    type: STEP_TYPE.IMAGES,
    name: "Images variations",
    stages: [DEFAULT_IMAGE_STAGE],
    enabled: hasImageVariationsStage([DEFAULT_IMAGE_STAGE]),
  },
  { id: WIZARD_STEP.BANNERS, name: "Banners", enabled: hasBannersStep },
  { id: WIZARD_STEP.GENERATE_VIDEO, name: "Video generation" },
];

/**
 * The "partnership-value" flow:
 * customer → script → scenes → (voiceover) → brand → audio → video.
 */
const PARTNERSHIP_VALUE_STEPS = [
  { id: WIZARD_STEP.SELECT_TEMPLATE, name: "Template selection" },
  { id: WIZARD_STEP.ACCOUNT_DETAILS, name: "Account details" },
  { id: WIZARD_STEP.CUSTOMER_DETAILS, name: "Customer details" },
  { id: WIZARD_STEP.CREATE_VIDEO, name: "Creating project", viewless: true },
  { id: WIZARD_STEP.BRANDING, name: "Brand" },
  {
    id: WIZARD_STEP.CAMPAIGN,
    name: "Script",
    preview: "partnership-value",
    contentLabel: "script",
    // Hide the per-field inline "Edit" buttons in the partnership-value
    // preview (the footer prompt itself stays available).
    canEditCampaign: true,
  },
  { id: WIZARD_STEP.SCENES, name: "Scenes" },
  { id: WIZARD_STEP.AUDIO, name: "Audio" },
  { id: WIZARD_STEP.VOICEOVER, name: "Voiceover", enabled: hasAudioStep },
  { id: WIZARD_STEP.GENERATE_VIDEO, name: "Video generation" },
];

const ONE_ADOBE_STEPS = [
  { id: WIZARD_STEP.SELECT_TEMPLATE, name: "Template selection" },
  { id: WIZARD_STEP.ACCOUNT_DETAILS, name: "Account details" },
  { id: WIZARD_STEP.CUSTOMER_DETAILS, name: "Customer details" },
  { id: WIZARD_STEP.CREATE_VIDEO, name: "Creating project", viewless: true },
  {
    id: WIZARD_STEP.STORY_FOUNDATION,
    type: STEP_TYPE.CAMPAIGN_VISUALS,
    name: "Story foundation",
    description:
      "Here is the beginning of your story - the product, trigger event, and the general campaign idea. All the remaining texts and images will be generated basing on this foundation.",
    preview: "one-adobe",
    contentLabel: "story foundation",
    canEditCampaign: true,
    promptHintsKey: "storyFoundationPromptHints",
    editScope: "story-foundation",
  },
  {
    id: WIZARD_STEP.BRANDING,
    name: "Brand",
  },
  {
    id: WIZARD_STEP.KEY_VISUALS,
    type: STEP_TYPE.CAMPAIGN_VISUALS,
    name: "Key visuals",
    description: "Let’s lock some key visual assets for this campaign",
    contentLabel: "key visuals",
    canEditCampaign: false,
    promptHintsKey: "keyVisualsPromptHints",
    editScope: "key-visuals",
  },
  {
    id: WIZARD_STEP.IMAGES_AND_TEXT,
    type: STEP_TYPE.CAMPAIGN_VISUALS,
    name: "Images & text",
    description:
      "Here are all the assets needed for your vision demo:  make sure they’re on-brand, realistic, and match the intent. Make edits as needed.",
    preview: "one-adobe",
    contentLabel: "images and text",
    editScope: "general",
    canEditCampaign: true,
  },
  { id: WIZARD_STEP.BANNERS, name: "Banners", enabled: hasBannersStep },
  { id: WIZARD_STEP.FIGMA_HANDOFF, name: "Handoff" },
];

const BRAND_VISIBILITY_STEPS = [
  { id: WIZARD_STEP.SELECT_TEMPLATE, name: "Template selection" },
  { id: WIZARD_STEP.ACCOUNT_DETAILS, name: "Account details" },
  { id: WIZARD_STEP.CUSTOMER_DETAILS, name: "Customer details" },
  { id: WIZARD_STEP.CREATE_VIDEO, name: "Creating project", viewless: true },
  {
    id: WIZARD_STEP.STORY_FOUNDATION,
    type: STEP_TYPE.CAMPAIGN_VISUALS,
    preview: "one-adobe",
    contentLabel: "story foundation",
    canEditCampaign: true,
    promptHintsKey: "storyFoundationPromptHints",
    editScope: "story-foundation",
  },
  {
    id: WIZARD_STEP.BRANDING,
    name: "Brand",
  },
  {
    id: WIZARD_STEP.KEY_VISUALS,
    type: STEP_TYPE.CAMPAIGN_VISUALS,
    contentLabel: "key visuals",
    canEditCampaign: false,
    showPromptFooter: false,
    promptHintsKey: "keyVisualsPromptHints",
    editScope: "key-visuals",
  },
  {
    id: WIZARD_STEP.IMAGES_AND_TEXT,
    type: STEP_TYPE.CAMPAIGN_VISUALS,
    preview: "one-adobe",
    contentLabel: "campaign images",
    editScope: "general",
    canEditCampaign: false,
    showPromptFooter: false,
  },
  { id: WIZARD_STEP.BANNERS, name: "Banners", enabled: hasBannersStep },
  { id: WIZARD_STEP.GENERATE_VIDEO, name: "Video generation" },
];

const STEPS_CONFIG = {
  "partnership-value": PARTNERSHIP_VALUE_STEPS,
  "one-adobe": ONE_ADOBE_STEPS,
  "brand-visibility": BRAND_VISIBILITY_STEPS,
};

/** The full (unfiltered) step list configured for a template id. */
export const getTemplateSteps = (templateId) =>
  STEPS_CONFIG[templateId] ?? DEFAULT_STEPS;

// What a template's `data.steps` entry may set on a step of its flow.
const TEMPLATE_STEP_FIELDS = {
  name: (value) => typeof value === "string" && !!value,
  description: (value) => typeof value === "string" && !!value,
  campaignStructure: Array.isArray,
};

// A step is resolved against the same entry once, so what is derived from it
// (memos, effects) does not start over each time the steps are resolved.
const resolvedStepsCache = new WeakMap();

/**
 * A configured step with what the template's data sets for it. The template
 * data may hold a `steps` map by step id — `{ name?, description?,
 * campaignStructure? }` — whose fields replace those of the step in
 * `STEPS_CONFIG`, falling back to them when unset. Any other option of a step
 * stays configured here.
 */
export const applyTemplateStepConfig = (step, templateData) => {
  const entry = templateData?.steps?.[step?.id];

  if (!entry || typeof entry !== "object") {
    return step;
  }

  let byEntry = resolvedStepsCache.get(step);

  if (!byEntry) {
    byEntry = new WeakMap();
    resolvedStepsCache.set(step, byEntry);
  }

  if (!byEntry.has(entry)) {
    const overrides = Object.fromEntries(
      Object.entries(TEMPLATE_STEP_FIELDS)
        .filter(([field, isValid]) => isValid(entry[field]))
        .map(([field]) => [field, entry[field]]),
    );

    byEntry.set(
      entry,
      Object.keys(overrides).length ? { ...step, ...overrides } : step,
    );
  }

  return byEntry.get(entry);
};

/**
 * The display name of a step under a given template. Pass `templateData` to
 * name it as the template's data does, and to also name the images stage steps
 * `resolveSteps` appends.
 */
export const getStepName = (templateId, stepId, templateData) => {
  const configured = getTemplateSteps(templateId).find(
    (step) => step.id === stepId,
  );

  if (configured) {
    return applyTemplateStepConfig(configured, templateData).name;
  }

  return templateData
    ? resolveSteps(templateId, templateData).find((step) => step.id === stepId)
        ?.name
    : undefined;
};

/**
 * The ordered steps that actually apply to a template, with conditional steps
 * (voiceover/images/banners) filtered out when their predicate is false.
 *
 * Images steps are resolved in a hybrid fashion: stages covered by an explicit
 * configured entry (`stages: [...]`) keep that entry's id, name and position,
 * while every remaining stage used by the template gets an auto-generated step
 * appended right after the last configured images step, ordered by first
 * appearance in `templateData.imageVariations`.
 *
 * Every step is resolved against the template's data first, which may set its
 * `name`, `description` and `campaignStructure` in `templateData.steps` (see
 * `applyTemplateStepConfig`).
 */
export const resolveSteps = (templateId, templateData) => {
  const configured = getTemplateSteps(templateId).map((step) =>
    applyTemplateStepConfig(step, templateData),
  );
  const steps = [];
  // Where auto-expanded stage steps belong: right after the last configured
  // images step — tracked even when that step itself is filtered out.
  let stageInsertIndex = -1;

  for (const step of configured) {
    if (!step.enabled || step.enabled(templateData)) {
      steps.push(step);
    }

    if (isImagesStep(step)) {
      stageInsertIndex = steps.length;
    }
  }

  if (stageInsertIndex === -1) {
    return steps;
  }

  const imagesSteps = configured.filter(isImagesStep);

  // An images step without `stages` renders every stage — nothing to expand.
  if (imagesSteps.some((step) => !step.stages)) {
    return steps;
  }

  const coveredStages = new Set(
    imagesSteps.flatMap((step) => step.stages ?? []),
  );

  const extraSteps = getImageStages(templateData)
    .filter((stage) => !coveredStages.has(stage))
    .map((stage) => ({
      id: makeImagesStepId(stage),
      type: STEP_TYPE.IMAGES,
      name: getStageStepName(stage),
      stages: [stage],
    }));

  steps.splice(stageInsertIndex, 0, ...extraSteps);

  return steps;
};

/** The resolved images steps (one per stage) for a template, in flow order. */
export const getImagesSteps = (templateId, templateData) =>
  resolveSteps(templateId, templateData).filter(isImagesStep);

/**
 * The images step a project should resume at: the first stage that still has
 * variations without a generated asset, or the last images step when every
 * stage is complete. `null` when the template has no images step at all.
 */
export const getResumeImagesStepId = (templateId, templateData, videoData) => {
  const steps = getImagesSteps(templateId, templateData);

  if (!steps.length) {
    return null;
  }

  const selectedCampaign = videoData?.campaigns?.find(
    (campaign) => campaign.id === videoData?.selectedCampaignIds?.[0],
  );

  const incomplete = steps.find((step) =>
    filterActiveVariations(
      templateData?.imageVariations,
      step.stages,
      selectedCampaign,
    ).some(
      (variation) =>
        !videoData?.assets?.some((asset) => asset.name === variation.key),
    ),
  );

  return (incomplete ?? steps[steps.length - 1]).id;
};

/**
 * Default per-step navigation actions, run by `handleNextStep` — never by
 * `useEffect`. Apply to every template unless overridden per `template.id` in
 * `TEMPLATE_STEP_ACTIONS` below.
 *
 * `enter` runs when navigating INTO a step; `leave` runs when navigating OUT of
 * it. Each action is pure configuration that operates on a `ctx` object the view
 * assembles (queries, mutations, and `run*` triggers):
 *
 *   - `isReady(ctx)`   — the step already has the data this action would produce.
 *                        The action is only worth running when this is `false`.
 *   - `isPending(ctx)` — the backing mutation is currently running.
 *   - `run(ctx)`       — trigger the mutation. Must be idempotent/self-guarding
 *                        because `handleNextStep` calls it unconditionally.
 *   - `pending`        — optional copy for the loading state shown while
 *                        `isPending` is true. `{ title, subtitle, icon }` are
 *                        forwarded to `Step.Loading`. Only used for viewless
 *                        steps; non-viewless steps render their own component.
 *   - `placeholder`    — copy for the unified empty-state (`IllustratedMessage`)
 *                        shown when an `enter` step is displayed but
 *                        `!isReady && !isPending` — e.g. after a page refresh
 *                        interrupted the mutation, leaving the step with no data
 *                        and nothing generating it. Not used for `leave`.
 */
/**
 * Whether the campaign step has produced what the steps after it need — shared
 * by every step that renders the selected campaign.
 */
const isCampaignReady = (ctx) => {
  if (!ctx.video.data?.id) {
    return false;
  }

  if (
    ctx.template.data?.campaignSelectionType === SELECTION_TYPE.NONE &&
    !ctx.selectedCampaign?.id
  ) {
    return false;
  } else if (!ctx.video.data?.campaigns?.length) {
    return false;
  }

  return true;
};

/** The One Adobe key visual the campaign's `KV-description` describes. */
export const KEY_VISUAL_KEY = "KV-primary";

/**
 * Whether the selected campaign still describes the key visual on the video.
 *
 * The key visual's "effective description" is the prompt it was generated
 * from or — when it was uploaded rather than generated — its image labeling.
 * The campaign is in sync once its `KV-description` equals it; the KV sync
 * writes that exact value back after adjusting the campaign, so the check is
 * stable across visits.
 */
export const getKeyVisualSyncState = (videoData, selectedCampaign) => {
  const asset = videoData?.assets?.find((a) => a?.name === KEY_VISUAL_KEY);

  if (!asset?.filePath) {
    return {
      asset,
      effectiveDescription: "",
      needsLabeling: false,
      isOutOfSync: false,
    };
  }

  const effectiveDescription =
    asset.prompt || asset.labeling?.description || "";

  return {
    asset,
    effectiveDescription,
    needsLabeling: !asset.labeling,
    isOutOfSync:
      !!effectiveDescription &&
      effectiveDescription !== (selectedCampaign?.["KV-description"] ?? ""),
  };
};

export const DEFAULT_STEP_ACTIONS = {
  [WIZARD_STEP.SELECT_TEMPLATE]: {
    enter: {
      isReady: () => true,
      isPending: () => false,
      run: (ctx) => {
        ctx.setSelectedTemplateKey(null);
      },
    },
  },
  [WIZARD_STEP.CREATE_VIDEO]: {
    enter: {
      isReady: (ctx) => !!ctx.video.data?.id,
      isPending: (ctx) => ctx.createVideo.isPending || ctx.video.isPending,
      run: (ctx) => {
        const id = ctx.ensureProjectId();
        ctx.runCreateVideo(id, (videoData) => {
          ctx.runGenerateBranding(videoData);
        });
      },
      pending: {
        title: "Creating your project",
        subtitle: "This may take a moment…",
      },
      placeholder: {
        title: "Oops, something went wrong",
        body: "But no worries. Let's give it another go.",
        action: "Try again",
      },
    },
  },
  [WIZARD_STEP.CAMPAIGN]: {
    enter: {
      isReady: isCampaignReady,
      isPending: (ctx) =>
        !ctx.video.data?.campaigns?.length && ctx.customerBranding.isPending,
      run: (ctx) => {
        ctx.runCreateCampaign(ctx.video?.data);
        ctx.resetPersonaCampaigns();
      },
      placeholder: {
        title: "Oops, something went wrong",
        body: "But no worries. Let's give it another go.",
        action: "Try again",
      },
    },
  },
  [WIZARD_STEP.CAMPAIGN_VISUALS]: {
    enter: {
      // Needs the campaign the visuals are written for — generated here too
      // when missing (e.g. after a reload interrupted it). The linked image
      // variations are generated by the step itself, which renders their
      // progress, so only the campaign counts as pending.
      isReady: isCampaignReady,
      isPending: (ctx) =>
        !ctx.video.data?.campaigns?.length && ctx.customerBranding.isPending,
      run: (ctx) => {
        if (!isCampaignReady(ctx)) {
          ctx.runCreateCampaign(ctx.video?.data);
        }

        ctx.imageVariations?.run();
      },
      placeholder: {
        title: "Oops, something went wrong",
        body: "But no worries. Let's give it another go.",
        action: "Try again",
      },
    },
  },
  [WIZARD_STEP.SCENES]: {
    enter: {
      // Ready once scenes exist.
      isReady: (ctx) => !!ctx.selectedCampaign?.scenes?.length,
      isPending: (ctx) =>
        !ctx.selectedCampaign?.scenes?.length && ctx.generateScenes.isPending,
      run: (ctx) => ctx.runGenerateScenes(),
      placeholder: {
        title: "Oops, something went wrong",
        body: "But no worries. Let's give it another go.",
        action: "Try again",
      },
    },
  },
  [WIZARD_STEP.BRANDING]: {
    enter: {
      isReady: (ctx) =>
        !!ctx.video.data?.id && !!ctx.video.data?.customer?.["Company-name"],
      isPending: (ctx) => ctx.checkCustomer.isPending,
      run: (ctx) => {
        ctx.runGenerateBranding();
      },
      placeholder: {
        title: "Oops, something went wrong",
        body: "But no worries. Let's give it another go.",
        action: "Try again",
      },
    },
  },
  [WIZARD_STEP.IMAGES]: {
    enter: {
      // The checks that used to gate the generation effect inside
      // `ImageVariations`: nothing left to generate for this step's stages,
      // every reference image resolved and uploaded, and no write of the video
      // still in flight.
      //
      // `ctx.imageVariations` is absent from the reduced readiness ctx the
      // wizard uses to resolve the step from the URL — there is no pipeline to
      // ask there, and images steps are reachable regardless, so it counts as
      // ready.
      isReady: (ctx) => {
        const imageVariations = ctx.imageVariations;

        if (!imageVariations) {
          return true;
        }

        return (
          imageVariations.isComplete &&
          !imageVariations.isReferenceMissing &&
          !imageVariations.references.isFetching &&
          !imageVariations.references.isError &&
          !imageVariations.isUploading &&
          !imageVariations.isSaving
        );
      },
      // Generating the variations is an asynchronous, multi-step process —
      // describing the references, generating the prompts, uploading them,
      // generating each image — and the step renders its own per-variation
      // progress, so the action itself never reports as pending.
      isPending: () => false,
      run: (ctx) => ctx.imageVariations?.run(),
    },
  },
  [WIZARD_STEP.BANNERS]: {
    enter: {
      // Ready when the template has no banners, the banner template is still
      // loading, or renditions already exist.
      isReady: (ctx) =>
        !ctx.template.data?.assetsTemplateId ||
        !ctx.assetsTemplate.isSuccess ||
        !!ctx.video.data?.templateAssetsRenditions?.length,
      isPending: (ctx) =>
        ctx.generateBanners.isPending || ctx.editCampaign.isPending,
      run: (ctx) => ctx.runBannersTransition(),
      placeholder: {
        title: "Oops, something went wrong",
        body: "But no worries. Let's give it another go.",
        action: "Try again",
      },
    },
  },
  [WIZARD_STEP.VOICEOVER]: {
    // Fire-and-forget: after the user leaves the voiceover step, reconcile the
    // muxed audio track in the background — but only when the voiceover config
    // actually changed (detected via the muxed asset's `muxSourceKey`).
    leave: {
      isReady: (ctx) => ctx.isVoiceoverMuxReady(),
      isPending: (ctx) => ctx.audioMux.isPending || ctx.uploadAssets.isPending,
      run: (ctx) => ctx.runVoiceoverMux(),
    },
  },
  [WIZARD_STEP.GENERATE_VIDEO]: {
    enter: {
      // Always "ready" — GenerateVideoStep owns its own progress/empty states,
      // so no placeholder. The render kicks off on entry.
      isReady: () => true,
      isPending: () => false,
      run: (ctx) => ctx.runGenerateVideo(),
    },
  },
};

/**
 * Per-template step-action overrides, keyed by `template.id` then step id.
 * A step is looked up on its exact id first, then on the id of the step family
 * whose default actions it shares (e.g. `images`, `campaign-visuals`).
 *
 * Each entry may supply `enter`/`leave` to customise the default action for
 * that step. Overrides are shallow-merged field-by-field, so a template can:
 *   - tweak a single field (e.g. `enter: { placeholder: {...} }`) while keeping
 *     the default `isReady`/`isPending`/`run`;
 *   - replace an action entirely by providing all fields;
 *   - add an action for a step that has no default; or
 *   - disable a default action with `enter: null` / `leave: null`.
 *
 * Example:
 *   "partnership-value": {
 *     [WIZARD_STEP.SCENES]: {
 *       enter: { placeholder: { action: "Storyboard scenes" } },
 *     },
 *   },
 */
export const TEMPLATE_STEP_ACTIONS = {
  "partnership-value": {},
  "one-adobe": {
    [WIZARD_STEP.KEY_VISUALS]: {
      // When the user regenerated or uploaded a different key visual, describe
      // it and have the LLM adjust the campaign to it — in the background, so
      // the next step can show its own loading state meanwhile.
      leave: {
        isReady: (ctx) => {
          const { needsLabeling, isOutOfSync } = getKeyVisualSyncState(
            ctx.video.data,
            ctx.selectedCampaign,
          );
          return !needsLabeling && !isOutOfSync;
        },
        isPending: (ctx) => !!ctx.syncKeyVisual?.isPending,
        run: (ctx) => ctx.runSyncKeyVisual?.(),
      },
    },
    [WIZARD_STEP.IMAGES_AND_TEXT]: {
      enter: {
        // The visuals are written for the campaign, so they wait for it to be
        // adjusted to the key visual chosen on the previous step.
        isPending: (ctx) =>
          DEFAULT_STEP_ACTIONS[WIZARD_STEP.CAMPAIGN_VISUALS].enter.isPending(
            ctx,
          ) || !!ctx.syncKeyVisual?.isPending,
      },
    },
  },
};

// Merge a template's per-action override onto the default action.
// `undefined` → inherit default; `null` → disable; object → field-level merge.
const mergeStepAction = (base, override) => {
  if (override === null) {
    return null;
  }
  if (override === undefined) {
    return base ?? null;
  }
  return { ...(base ?? {}), ...override };
};

/**
 * The resolved `{ enter, leave }` actions for a configured step (a bare id
 * for a step that declares nothing else) under a given template.
 */
const resolveStepActions = (step, templateId) => {
  const entry = toStepEntry(step);
  // Every images step shares the `images` step's actions, and a step of a
  // family without actions of its own shares the family's.
  const familyId = getStepFamilyId(entry);
  const baseId =
    familyId in DEFAULT_STEP_ACTIONS || !isCampaignVisualsStep(entry)
      ? familyId
      : WIZARD_STEP.CAMPAIGN_VISUALS;
  const base = DEFAULT_STEP_ACTIONS[baseId] ?? {};
  // A template may override a step on its own id, or its whole family.
  const templateActions = TEMPLATE_STEP_ACTIONS[templateId];
  const override =
    templateActions?.[entry?.id] ??
    templateActions?.[familyId] ??
    templateActions?.[baseId] ??
    {};
  return {
    enter: mergeStepAction(base.enter, override.enter),
    leave: mergeStepAction(base.leave, override.leave),
  };
};

/** The entering action configured for a step under a template, or `null`. */
export const getStepEnterAction = (step, templateId) =>
  resolveStepActions(step, templateId).enter;

/** The leaving action configured for a step under a template, or `null`. */
export const getStepLeaveAction = (step, templateId) =>
  resolveStepActions(step, templateId).leave;

/** The two entry steps that precede the wizard proper (not shown in stepper). */
export const PRE_FLOW_STEPS = [
  WIZARD_STEP.SELECT_TEMPLATE,
  WIZARD_STEP.ACCOUNT_DETAILS,
];

/**
 * Centralised Continue-button copy, derived from the step being navigated to
 * so it stays correct regardless of which conditional steps are enabled.
 */
export const getContinueLabel = (nextStep) => {
  const nextFamilyId = getStepFamilyId(nextStep);

  if (nextFamilyId === WIZARD_STEP.IMAGES) {
    return "Confirm & proceed";
  }
  if (nextFamilyId === WIZARD_STEP.BANNERS) {
    return "Generate banners";
  }
  if (nextFamilyId === WIZARD_STEP.GENERATE_VIDEO) {
    return "Generate video";
  }
  return "Continue";
};
